import type { Transaction } from 'dexie'
import { isBlob } from '@/db/blob'
import { CHECKLIST_ITEMS } from '@/db/checklist'
import { createDefaultSettings } from '@/db/defaults'
import { APP_VERSION } from '@/app/version'
import {
  inspectionSearchFields,
  normalizePhone,
  normalizeSearchText,
  toActiveKey,
} from '@/db/normalization'
import { createUuid } from '@/db/uuid'
import { DATABASE_SCHEMA_VERSION, INSPECTION_SCHEMA_VERSION } from '@/db/version'
import type {
  AuditLog,
  Client,
  Inspection,
  InspectionPriority,
  InspectionResult,
  Machine,
  Photo,
  PhotoPreview,
  Settings,
  Signature,
  Technician,
} from '@/db/types'

type LegacyRecord = Record<string, unknown>

function valueAsString(record: LegacyRecord, key: string, fallback = ''): string {
  const value = record[key]
  return typeof value === 'string' ? value : fallback
}

function optionalString(record: LegacyRecord, key: string): string | undefined {
  const value = valueAsString(record, key).trim()
  return value ? value : undefined
}

function valueAsNumber(record: LegacyRecord, key: string, fallback = 0): number {
  const value = record[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function valueAsBlob(record: LegacyRecord, key: string): Blob | undefined {
  const value = record[key]
  return isBlob(value) ? value : undefined
}

function valueAsRecord(record: LegacyRecord, key: string): LegacyRecord {
  const value = record[key]
  return typeof value === 'object' && value !== null ? (value as LegacyRecord) : {}
}

function isActive(record: LegacyRecord): boolean {
  if (typeof record.active === 'boolean') return record.active
  return valueAsString(record, 'activity', 'ACTIVE') !== 'INACTIVE'
}

function mapStatus(value: string): Inspection['status'] {
  if (value === 'COMPLETED' || value === 'completed') return 'completed'
  if (value === 'CANCELED' || value === 'CANCELLED' || value === 'cancelled') return 'cancelled'
  return 'draft'
}

function mapPriority(value: string): InspectionPriority {
  const priorities: Record<string, InspectionPriority> = {
    URGENT_STOP_IMMEDIATELY: 'urgent_stop_immediately',
    SCHEDULE_WITHIN_15_DAYS: 'schedule_within_15_days',
    MONITOR: 'monitor',
    PENDING: 'pending',
  }
  const normalized = value.toLowerCase()
  if (
    normalized === 'urgent_stop_immediately' ||
    normalized === 'schedule_within_15_days' ||
    normalized === 'monitor' ||
    normalized === 'pending'
  ) {
    return normalized
  }
  return priorities[value] ?? 'pending'
}

function migrateClient(record: LegacyRecord): Client {
  const active = isActive(record)
  const companyName = valueAsString(record, 'companyName', 'Cliente migrado')
  const farmOrSite = valueAsString(record, 'farmOrSite', 'Não informado')
  const phone = valueAsString(record, 'phone', '00000000')
  const migrated: Client = {
    id: valueAsString(record, 'id'),
    code: valueAsString(record, 'code'),
    companyName,
    normalizedCompanyName: normalizeSearchText(companyName),
    farmOrSite,
    normalizedFarmOrSite: normalizeSearchText(farmOrSite),
    contactName: valueAsString(record, 'contactName', 'Não informado'),
    phone,
    normalizedPhone: normalizePhone(phone),
    active,
    activeKey: toActiveKey(active),
    createdAt: valueAsString(record, 'createdAt', new Date().toISOString()),
    updatedAt: valueAsString(record, 'updatedAt', new Date().toISOString()),
  }
  const email = optionalString(record, 'email')
  const document = optionalString(record, 'document') ?? optionalString(record, 'taxId')
  const address = optionalString(record, 'address')
  const notes = optionalString(record, 'notes')
  if (email) migrated.email = email
  if (document) migrated.document = document
  if (address) migrated.address = address
  if (notes) migrated.notes = notes
  return migrated
}

function migrateMachine(record: LegacyRecord): Machine {
  const active = isActive(record)
  const equipment = valueAsString(record, 'equipment', 'Máquina migrada')
  const brand = valueAsString(record, 'brand', 'Não informada')
  const model = valueAsString(record, 'model', 'Não informado')
  const serialChassis =
    valueAsString(record, 'serialChassis') ||
    valueAsString(record, 'serialOrChassis', 'Não informado')
  const migrated: Machine = {
    id: valueAsString(record, 'id'),
    code: valueAsString(record, 'code'),
    clientId: valueAsString(record, 'clientId'),
    equipment,
    normalizedEquipment: normalizeSearchText(equipment),
    brand,
    normalizedBrand: normalizeSearchText(brand),
    model,
    normalizedModel: normalizeSearchText(model),
    serialChassis,
    normalizedSerialChassis: normalizeSearchText(serialChassis),
    currentHourmeter: valueAsNumber(
      record,
      'currentHourmeter',
      valueAsNumber(record, 'currentHourMeter'),
    ),
    notes: valueAsString(record, 'notes'),
    active,
    activeKey: toActiveKey(active),
    createdAt: valueAsString(record, 'createdAt', new Date().toISOString()),
    updatedAt: valueAsString(record, 'updatedAt', new Date().toISOString()),
  }
  const year = valueAsNumber(record, 'year')
  const photoId = optionalString(record, 'photoId') ?? optionalString(record, 'coverPhotoId')
  if (year) migrated.year = year
  if (photoId) migrated.photoId = photoId
  return migrated
}

function migrateTechnician(record: LegacyRecord): Technician {
  const active = isActive(record)
  const name = valueAsString(record, 'name', 'Técnico migrado')
  const phone = valueAsString(record, 'phone', '00000000')
  const migrated: Technician = {
    id: valueAsString(record, 'id'),
    name,
    normalizedName: normalizeSearchText(name),
    role: valueAsString(record, 'role', 'Técnico'),
    phone,
    normalizedPhone: normalizePhone(phone),
    active,
    activeKey: toActiveKey(active),
    createdAt: valueAsString(record, 'createdAt', new Date().toISOString()),
    updatedAt: valueAsString(record, 'updatedAt', new Date().toISOString()),
  }
  const signatureId =
    optionalString(record, 'signatureId') ?? optionalString(record, 'defaultSignatureId')
  if (signatureId) migrated.signatureId = signatureId
  return migrated
}

function migrateInspection(record: LegacyRecord): Inspection {
  const status = mapStatus(valueAsString(record, 'status'))
  const legacyClientSnapshot = valueAsRecord(record, 'clientSnapshot')
  const legacyMachineSnapshot = valueAsRecord(record, 'machineSnapshot')
  const legacyTechnicianSnapshot = valueAsRecord(record, 'technicianSnapshot')
  const migratedClientSnapshot: Inspection['clientSnapshot'] = {
    code: valueAsString(legacyClientSnapshot, 'code', 'CLI-000000'),
    companyName: valueAsString(legacyClientSnapshot, 'companyName', 'Cliente migrado'),
    farmOrSite: valueAsString(legacyClientSnapshot, 'farmOrSite', 'Não informado'),
    contactName: valueAsString(legacyClientSnapshot, 'contactName', 'Não informado'),
    phone: valueAsString(legacyClientSnapshot, 'phone', '00000000'),
  }
  const snapshotEmail = optionalString(legacyClientSnapshot, 'email')
  const snapshotDocument =
    optionalString(legacyClientSnapshot, 'document') ??
    optionalString(legacyClientSnapshot, 'taxId')
  const snapshotAddress = optionalString(legacyClientSnapshot, 'address')
  if (snapshotEmail) migratedClientSnapshot.email = snapshotEmail
  if (snapshotDocument) migratedClientSnapshot.document = snapshotDocument
  if (snapshotAddress) migratedClientSnapshot.address = snapshotAddress

  const migratedMachineSnapshot: Inspection['machineSnapshot'] = {
    code: valueAsString(legacyMachineSnapshot, 'code', 'MAQ-000000'),
    equipment: valueAsString(legacyMachineSnapshot, 'equipment', 'Máquina migrada'),
    brand: valueAsString(legacyMachineSnapshot, 'brand', 'Não informada'),
    model: valueAsString(legacyMachineSnapshot, 'model', 'Não informado'),
    serialChassis:
      valueAsString(legacyMachineSnapshot, 'serialChassis') ||
      valueAsString(legacyMachineSnapshot, 'serialOrChassis', 'Não informado'),
  }
  const snapshotYear = valueAsNumber(legacyMachineSnapshot, 'year')
  if (snapshotYear) migratedMachineSnapshot.year = snapshotYear

  const migratedTechnicianSnapshot: Inspection['technicianSnapshot'] = {
    name: valueAsString(legacyTechnicianSnapshot, 'name', 'Técnico migrado'),
    role: valueAsString(legacyTechnicianSnapshot, 'role', 'Técnico'),
    phone: valueAsString(legacyTechnicianSnapshot, 'phone', '00000000'),
  }
  const migrated: Inspection = {
    id: valueAsString(record, 'id'),
    reportNumber: valueAsString(record, 'reportNumber'),
    clientId: valueAsString(record, 'clientId'),
    machineId: valueAsString(record, 'machineId'),
    technicianId: valueAsString(record, 'technicianId'),
    clientSnapshot: migratedClientSnapshot,
    machineSnapshot: migratedMachineSnapshot,
    technicianSnapshot: migratedTechnicianSnapshot,
    ...inspectionSearchFields({
      clientSnapshot: migratedClientSnapshot,
      machineSnapshot: migratedMachineSnapshot,
      technicianSnapshot: migratedTechnicianSnapshot,
    }),
    inspectionDate: valueAsString(record, 'inspectionDate'),
    hourmeter: valueAsNumber(record, 'hourmeter', valueAsNumber(record, 'hourMeter')),
    status,
    currentStep: 1,
    diagnosis: valueAsString(record, 'diagnosis'),
    priority: mapPriority(valueAsString(record, 'priority', 'PENDING')),
    authorization:
      valueAsString(record, 'authorization') === 'yes' ||
      valueAsString(record, 'authorizationDecision') === 'YES'
        ? 'yes'
        : valueAsString(record, 'authorization') === 'no' ||
            valueAsString(record, 'authorizationDecision') === 'NO'
          ? 'no'
          : 'unset',
    clientSignatureWaiverReason: '',
    responsibleName:
      valueAsString(record, 'responsibleName') || valueAsString(record, 'authorizedPersonName'),
    createdAt: valueAsString(record, 'createdAt', new Date().toISOString()),
    updatedAt: valueAsString(record, 'updatedAt', new Date().toISOString()),
    revisionNumber: 0,
    schemaVersion: INSPECTION_SCHEMA_VERSION,
  }
  const optionalFields = [
    'clientSignatureId',
    'technicianSignatureId',
    'completedAt',
    'cancelledAt',
    'cancellationReason',
    'sourceInspectionId',
  ] as const
  for (const field of optionalFields) {
    const value = optionalString(record, field)
    if (value) Object.assign(migrated, { [field]: value })
  }
  const canceledAt = optionalString(record, 'canceledAt')
  if (canceledAt) migrated.cancelledAt = canceledAt
  return migrated
}

function migrateResult(record: LegacyRecord): InspectionResult {
  const itemId =
    valueAsString(record, 'checklistItemId') || valueAsString(record, 'itemCode', '1.1')
  const template = CHECKLIST_ITEMS.find((item) => item.id === itemId) ?? CHECKLIST_ITEMS[0]
  if (!template) throw new Error('Checklist oficial indisponível durante migration.')
  const rawStatus = valueAsString(record, 'status', 'unanswered').toLowerCase()
  const status = ['good', 'attention', 'critical'].includes(rawStatus)
    ? (rawStatus as InspectionResult['status'])
    : 'unanswered'
  return {
    id: valueAsString(record, 'id'),
    inspectionId: valueAsString(record, 'inspectionId'),
    checklistItemId: itemId,
    checklistItemLabel: valueAsString(record, 'checklistItemLabel', template.label),
    itemOrder: valueAsNumber(record, 'itemOrder', template.order),
    systemId:
      valueAsString(record, 'systemId') || valueAsString(record, 'systemCode', template.systemId),
    systemLabel: valueAsString(record, 'systemLabel', template.systemLabel),
    systemOrder: valueAsNumber(record, 'systemOrder', template.systemOrder),
    status,
    observation: valueAsString(record, 'observation'),
    createdAt: valueAsString(record, 'createdAt', new Date().toISOString()),
    updatedAt: valueAsString(record, 'updatedAt', new Date().toISOString()),
  }
}

async function replaceTable<T>(transaction: Transaction, tableName: string, records: T[]) {
  const table = transaction.table<T, string>(tableName)
  await table.clear()
  if (records.length) await table.bulkPut(records)
}

export async function migrateVersion1To2(transaction: Transaction): Promise<void> {
  const [clients, machines, technicians, inspections, results, settings, photos] =
    await Promise.all([
      transaction.table<LegacyRecord, string>('clients').toArray(),
      transaction.table<LegacyRecord, string>('machines').toArray(),
      transaction.table<LegacyRecord, string>('technicians').toArray(),
      transaction.table<LegacyRecord, string>('inspections').toArray(),
      transaction.table<LegacyRecord, string>('inspectionResults').toArray(),
      transaction.table<LegacyRecord, string>('settings').toArray(),
      transaction.table<LegacyRecord, string>('photos').toArray(),
    ])

  await replaceTable(transaction, 'clients', clients.map(migrateClient))
  await replaceTable(transaction, 'machines', machines.map(migrateMachine))
  await replaceTable(transaction, 'technicians', technicians.map(migrateTechnician))
  await replaceTable(transaction, 'inspections', inspections.map(migrateInspection))
  await replaceTable(transaction, 'inspectionResults', results.map(migrateResult))

  const migratedInspectionPhotos: Photo[] = photos.flatMap((record) => {
    const blob = valueAsBlob(record, 'blob')
    const inspectionId = optionalString(record, 'inspectionId')
    if (!blob || !inspectionId) return []
    const category =
      valueAsString(record, 'category') === 'item' || valueAsString(record, 'scope') === 'ITEM'
        ? 'item'
        : 'general'
    const photo: Photo = {
      id: valueAsString(record, 'id', createUuid()),
      inspectionId,
      category,
      blob,
      mimeType: valueAsString(record, 'mimeType', 'image/jpeg'),
      width: valueAsNumber(record, 'width', 1),
      height: valueAsNumber(record, 'height', 1),
      size: valueAsNumber(record, 'size', valueAsNumber(record, 'sizeBytes', blob.size)),
      caption: valueAsString(record, 'caption'),
      order: valueAsNumber(record, 'order'),
      createdAt: valueAsString(record, 'createdAt', new Date().toISOString()),
      updatedAt: valueAsString(
        record,
        'updatedAt',
        valueAsString(record, 'createdAt', new Date().toISOString()),
      ),
    }
    const resultId = optionalString(record, 'resultId')
    if (resultId) photo.resultId = resultId
    return [photo]
  })
  await replaceTable(transaction, 'photos', migratedInspectionPhotos)

  const existingSettings = settings.find((record) => record.key === 'app')
  const migratedSettings: Settings = existingSettings
    ? {
        ...createDefaultSettings(),
        companyName: valueAsString(existingSettings, 'companyName', 'Real Máquinas'),
        phone: valueAsString(existingSettings, 'phone', '(63) 98107-2998'),
        instagram: valueAsString(existingSettings, 'instagram', '@realmaquinasto'),
        email: valueAsString(existingSettings, 'email', 'realmaquinasto@gmail.com'),
        qrCodeTarget: valueAsString(existingSettings, 'qrCodeTarget'),
        lastBackupAt: optionalString(existingSettings, 'lastBackupAt'),
        schemaVersion: DATABASE_SCHEMA_VERSION,
        updatedAt: valueAsString(existingSettings, 'updatedAt', new Date().toISOString()),
      }
    : createDefaultSettings()
  if (!migratedSettings.lastBackupAt) delete migratedSettings.lastBackupAt
  await replaceTable(transaction, 'settings', [migratedSettings])

  const machinePhotos = await transaction.table<LegacyRecord, string>('machinePhotos').toArray()
  const migratedPhotos: Photo[] = machinePhotos.flatMap((record) => {
    const blob = valueAsBlob(record, 'blob')
    if (!blob) return []
    return [
      {
        id: valueAsString(record, 'id', createUuid()),
        machineId: valueAsString(record, 'machineId'),
        category: 'general',
        blob,
        mimeType: valueAsString(record, 'mimeType', 'image/jpeg'),
        width: valueAsNumber(record, 'width', 1),
        height: valueAsNumber(record, 'height', 1),
        size: valueAsNumber(record, 'size', valueAsNumber(record, 'sizeBytes', blob.size)),
        caption: '',
        order: 0,
        createdAt: valueAsString(record, 'createdAt', new Date().toISOString()),
        updatedAt: valueAsString(
          record,
          'updatedAt',
          valueAsString(record, 'createdAt', new Date().toISOString()),
        ),
      },
    ]
  })
  if (migratedPhotos.length) {
    await transaction.table<Photo, string>('photos').bulkPut(migratedPhotos)
  }

  const events = await transaction.table<LegacyRecord, string>('inspectionEvents').toArray()
  const backupHistory = await transaction.table<LegacyRecord, string>('backupHistory').toArray()
  const auditLogs: AuditLog[] = [...events, ...backupHistory].map((record) => ({
    id: valueAsString(record, 'id', createUuid()),
    entityType: record.inspectionId ? 'inspection' : 'demoData',
    entityId: valueAsString(record, 'inspectionId', valueAsString(record, 'id', 'legacy')),
    action: valueAsString(record, 'type') === 'CANCELED' ? 'cancel' : 'update',
    occurredAt:
      valueAsString(record, 'at') || valueAsString(record, 'updatedAt', new Date().toISOString()),
    changes: {
      migratedFrom: record.inspectionId ? 'inspectionEvents' : 'backupHistory',
      legacyRecord: record,
    },
  }))
  if (auditLogs.length) {
    await transaction.table<AuditLog, string>('auditLogs').bulkPut(auditLogs)
  }

  const signatures = await transaction.table<LegacyRecord, string>('signatures').toArray()
  const migratedSignatures: Signature[] = signatures.flatMap((record) => {
    const blob = valueAsBlob(record, 'blob')
    if (!blob) return []
    const signature: Signature = {
      id: valueAsString(record, 'id'),
      type:
        valueAsString(record, 'type') === 'client' || valueAsString(record, 'kind') === 'CUSTOMER'
          ? 'client'
          : 'technician',
      blob,
      mimeType: valueAsString(record, 'mimeType', 'image/png'),
      createdAt:
        valueAsString(record, 'createdAt') ||
        valueAsString(record, 'signedAt', new Date().toISOString()),
    }
    const inspectionId = optionalString(record, 'inspectionId')
    const technicianId = optionalString(record, 'technicianId')
    if (inspectionId) signature.inspectionId = inspectionId
    if (technicianId) signature.technicianId = technicianId
    return [signature]
  })
  await replaceTable(transaction, 'signatures', migratedSignatures)
}

export async function migrateVersion3To4(transaction: Transaction): Promise<void> {
  const inspections = transaction.table<LegacyRecord, string>('inspections')
  await inspections.toCollection().modify((record) => {
    const step = valueAsNumber(record, 'currentStep', 1)
    record.currentStep = Number.isInteger(step) && step >= 1 && step <= 7 ? step : 1
    record.schemaVersion = INSPECTION_SCHEMA_VERSION
  })

  const settings = transaction.table<LegacyRecord, string>('settings')
  await settings.toCollection().modify((record) => {
    record.schemaVersion = DATABASE_SCHEMA_VERSION
  })
}

export async function migrateVersion4To5(transaction: Transaction): Promise<void> {
  const photoTable = transaction.table<LegacyRecord, string>('photos')
  const legacyPhotos = await photoTable.toArray()
  await photoTable
    .filter((record) => !optionalString(record, 'updatedAt'))
    .modify((record) => {
      record.updatedAt = valueAsString(
        record,
        'updatedAt',
        valueAsString(record, 'createdAt', new Date().toISOString()),
      )
    })

  const previews: PhotoPreview[] = legacyPhotos.flatMap((record) => {
    const inspectionId = optionalString(record, 'inspectionId')
    const blob = valueAsBlob(record, 'blob')
    if (!inspectionId || !blob) return []
    const id = valueAsString(record, 'id')
    const createdAt = valueAsString(record, 'createdAt', new Date().toISOString())
    const category = valueAsString(record, 'category') === 'item' ? 'item' : 'general'
    const preview: PhotoPreview = {
      id,
      photoId: id,
      inspectionId,
      category,
      thumbnailBlob: blob,
      thumbnailMimeType: valueAsString(
        record,
        'mimeType',
        'image/jpeg',
      ) as PhotoPreview['thumbnailMimeType'],
      thumbnailWidth: valueAsNumber(record, 'width', 1),
      thumbnailHeight: valueAsNumber(record, 'height', 1),
      thumbnailSize: valueAsNumber(record, 'size', blob.size),
      fullMimeType: valueAsString(record, 'mimeType', 'image/jpeg'),
      fullWidth: valueAsNumber(record, 'width', 1),
      fullHeight: valueAsNumber(record, 'height', 1),
      fullSize: valueAsNumber(record, 'size', blob.size),
      caption: valueAsString(record, 'caption'),
      order: valueAsNumber(record, 'order'),
      createdAt,
      updatedAt: valueAsString(record, 'updatedAt', createdAt),
    }
    const resultId = optionalString(record, 'resultId')
    if (resultId) preview.resultId = resultId
    return [preview]
  })
  if (previews.length) {
    await transaction.table<PhotoPreview, string>('photoPreviews').bulkPut(previews)
  }

  const settings = transaction.table<LegacyRecord, string>('settings')
  await settings.toCollection().modify((record) => {
    record.photoMaxInputBytes = valueAsNumber(record, 'photoMaxInputBytes', 10 * 1024 * 1024)
    record.photoMaxDimension = valueAsNumber(record, 'photoMaxDimension', 1600)
    record.photoJpegQuality = valueAsNumber(record, 'photoJpegQuality', 0.76)
    record.schemaVersion = DATABASE_SCHEMA_VERSION
  })
}

export async function migrateVersion5To6(transaction: Transaction): Promise<void> {
  const inspections = transaction.table<LegacyRecord, string>('inspections')
  await inspections.toCollection().modify((record) => {
    const authorization = valueAsString(record, 'authorization', 'unset')
    if ((authorization === 'yes' || authorization === 'no') && !record.authorizationAt) {
      record.authorizationAt = valueAsString(
        record,
        'updatedAt',
        valueAsString(record, 'createdAt', new Date().toISOString()),
      )
    }
    record.clientSignatureWaiverReason = valueAsString(record, 'clientSignatureWaiverReason')
    record.revisionNumber = Math.max(0, Math.trunc(valueAsNumber(record, 'revisionNumber')))
    record.schemaVersion = INSPECTION_SCHEMA_VERSION
  })

  const settings = transaction.table<LegacyRecord, string>('settings')
  await settings.toCollection().modify((record) => {
    record.schemaVersion = DATABASE_SCHEMA_VERSION
  })
}

export async function migrateVersion6To7(transaction: Transaction): Promise<void> {
  const inspections = transaction.table<Inspection, string>('inspections')
  await inspections.toCollection().modify((record) => {
    Object.assign(record, inspectionSearchFields(record))
    record.schemaVersion = INSPECTION_SCHEMA_VERSION
  })

  const settings = transaction.table<LegacyRecord, string>('settings')
  await settings.toCollection().modify((record) => {
    record.schemaVersion = DATABASE_SCHEMA_VERSION
  })
}

export async function migrateVersion7To8(transaction: Transaction): Promise<void> {
  const settings = transaction.table<LegacyRecord, string>('settings')
  await settings.toCollection().modify((record) => {
    record.appVersion = APP_VERSION
    record.schemaVersion = DATABASE_SCHEMA_VERSION
  })
}
